// src/components/dashboard/disbursement/hierarchy.js
// Porta JS de `src/project/disbursement/hierarchy.js` do back-end.

export const LEVEL_OFFSETS = Object.freeze({
  building: 0, unit: 3, cell: 5, step: 8, substep: 11, service: 14,
});
const LEVEL_BY_OFFSET = new Map(
  Object.entries(LEVEL_OFFSETS).map(([level, offset]) => [offset, level]),
);
export const PARENT_CUT = Object.freeze({ service: 3, substep: 3, step: 3, cell: 2, unit: 3 });

export const levelOf = (wbsCodeId, root) => {
  const code = String(wbsCodeId ?? "");
  const base = String(root ?? "");
  if (!base || !code.startsWith(base)) return null;
  return LEVEL_BY_OFFSET.get(code.length - base.length) ?? null;
};

// Deslocamento do nível de apropriação, ou `null` quando o nível não é reconhecido.
// Um nível desconhecido NUNCA cai para `service`: cair em silêncio é exatamente o defeito
// que descartava 100% do comprometido das obras não-service.
export const offsetForLevel = (level) => {
  const key = typeof level === "string" ? level : "";
  return Object.prototype.hasOwnProperty.call(LEVEL_OFFSETS, key) ? LEVEL_OFFSETS[key] : null;
};

// O ancestral de `wbsCodeId` no nível pedido — ou o próprio nó, quando já está nele.
export const nodeAtLevel = (wbsCodeId, root, level) => {
  const offset = offsetForLevel(level);
  if (offset === null) return null;
  const code = String(wbsCodeId ?? "");
  const base = String(root ?? "");
  if (!base || !code.startsWith(base)) return null;
  if (code.length - base.length < offset) return null;
  return code.slice(0, base.length + offset);
};

export const ancestorsOf = (wbsCodeId, root) => {
  const result = [];
  let current = String(wbsCodeId ?? "");
  let level = levelOf(current, root);
  while (level && level !== "building") {
    current = current.slice(0, current.length - PARENT_CUT[level]);
    level = levelOf(current, root);
    if (!level) break;
    result.push(current);
  }
  return result;
};

export const monthKey = (year, month) => {
  if (!Number.isSafeInteger(year) || year < 1900 || year > 9999) {
    throw new RangeError(`year must be a safe integer in [1900, 9999]: ${year}`);
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`month must be an integer in [1, 12]: ${month}`);
  }
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
};

export const parseMonthKey = (key) => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(key ?? ""));
  if (!match) throw new RangeError(`invalid month key: ${key}`);
  return { year: Number(match[1]), month: Number(match[2]) };
};

export const shiftMonth = (key, offset) => {
  const { year, month } = parseMonthKey(key);
  const index = year * 12 + (month - 1) + offset;
  return monthKey(Math.floor(index / 12), (index % 12) + 1);
};

export const compareMonthKey = (left, right) => (
  left < right ? -1 : left > right ? 1 : 0
);

// Só acumula: os valores já vieram validados do laço único abaixo.
const addInto = (target, key, cents) => target.set(key, (target.get(key) ?? 0) + cents);

export const rollUpSeries = (leafSeriesByWbs, root) => {
  if (typeof root !== "string" || root.length === 0) {
    throw new RangeError(`root must be a non-empty string: ${root}`);
  }
  if (!(leafSeriesByWbs instanceof Map)) {
    throw new RangeError("leafSeriesByWbs must be a Map<string, Map<monthKey, cents>>");
  }

  // Passo único de validação: nível da folha, tipo da série, forma de cada valor e
  // ausência de ancestral na própria entrada — UMA vez por folha, antes de qualquer
  // acumulação. Entrada de níveis misturados (folha e ancestral juntos) contaria duas
  // vezes, então é erro de chamador, não algo a "somar mesmo assim".
  const leaves = [];
  for (const [wbsCodeId, series] of leafSeriesByWbs) {
    if (levelOf(wbsCodeId, root) === null) {
      throw new RangeError(`wbsCodeId is not under root ${root}: ${wbsCodeId}`);
    }
    if (!(series instanceof Map)) {
      throw new RangeError(`series for leaf ${wbsCodeId} must be a Map<monthKey, cents>`);
    }
    const entries = [...series];
    for (const [, cents] of entries) {
      if (!Number.isSafeInteger(cents)) {
        throw new RangeError("series values must be integer cents");
      }
    }
    const ancestors = ancestorsOf(wbsCodeId, root);
    for (const ancestor of ancestors) {
      if (leafSeriesByWbs.has(ancestor)) {
        throw new RangeError(
          `leaf ${wbsCodeId} has an ancestor leaf (${ancestor}) present in the same input`
          + " — mixed-level input would double-count",
        );
      }
    }
    leaves.push({ wbsCodeId, entries, ancestors });
  }

  const result = new Map();
  for (const { wbsCodeId, entries, ancestors } of leaves) {
    for (const node of [wbsCodeId, ...ancestors]) {
      if (!result.has(node)) result.set(node, new Map());
      const target = result.get(node);
      for (const [key, cents] of entries) addInto(target, key, cents);
    }
  }
  for (const [node, series] of result) {
    result.set(node, new Map([...series].sort(([a], [b]) => compareMonthKey(a, b))));
  }
  return result;
};
